/**
 * Ingredient Grid — product filtering, mobile carousel rail, show-more, and a per-section
 * detail dialog. Extracted from an inline section <script> so the section markup stays
 * declarative and the logic is cacheable and lintable.
 *
 * Config is read from the root element's data-* attributes (written by the section), so this
 * file carries no Liquid. One controller per root, keyed by the root element, is aborted and
 * rebuilt on theme-editor re-render.
 */
(function () {
  if (window.__ingredientGridInit) return;
  window.__ingredientGridInit = true;

  const controllers = new Map();

  function readConfig(root) {
    return {
      initialCount: parseInt(root.dataset.initialCount, 10) || 12,
      enableShowMore: root.dataset.enableShowMore === 'true',
      isCarousel: root.dataset.isCarousel === 'true',
      labels: {
        more: root.dataset.labelMore || 'Show more',
        less: root.dataset.labelLess || 'Show less',
      },
    };
  }

  function initRoot(root) {
    if (!root) return;
    // A prior controller for this root (theme-editor re-render) must be aborted: its
    // listeners bind to DOM nodes this init replaces and can never be revived.
    teardownRoot(root);

    const grid = root.querySelector('[data-grid]');
    if (!grid) return;

    const CONFIG = readConfig(root);
    const items = [...root.querySelectorAll('[data-ingredient]')];
    const tabs = [...root.querySelectorAll('[data-filter]')];
    const showMoreWrap = root.querySelector('[data-show-more]');
    const showMoreBtn = root.querySelector('[data-show-more-btn]');
    const showMoreLabel = root.querySelector('[data-show-more-label]');
    const emptyEl = root.querySelector('[data-empty]');
    const statusEl = root.querySelector('[data-filter-status]');

    const controller = new AbortController();
    const { signal } = controller;
    controllers.set(root, controller);

    // The rail only exists below 750px, so this is a media query, not a measurement.
    // matchMedia also ignores the mobile URL-bar resize churn a debounced resize listener
    // would fire on.
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const railActive = () => CONFIG.isCarousel && mobileQuery.matches;

    let isExpanded = false;
    const activeTab = root.querySelector('[data-filter].is-active');
    let activeFilter = activeTab ? activeTab.dataset.filter : 'all';

    const matches = (item) =>
      activeFilter === 'all' || (item.dataset.products || '').split(' ').includes(activeFilter);

    function update(announce) {
      const visible = items.filter(matches);
      // In rail mode every matching card is present — you swipe, not expand. Capping there
      // would hide cards behind a button AND a scroll.
      const limit = CONFIG.enableShowMore && !railActive() ? CONFIG.initialCount : Infinity;
      const overLimit = visible.length > limit;

      items.forEach((item) => {
        item.hidden = !matches(item);
      });
      if (overLimit && !isExpanded) {
        visible.forEach((item, i) => {
          item.hidden = i >= limit;
        });
      }

      if (showMoreWrap) showMoreWrap.hidden = !overLimit;
      if (showMoreBtn) showMoreBtn.setAttribute('aria-expanded', String(isExpanded));
      if (showMoreLabel) {
        showMoreLabel.textContent = isExpanded ? CONFIG.labels.less : CONFIG.labels.more;
      }
      if (emptyEl) emptyEl.hidden = visible.length > 0;

      // Announce the result of a filter change through the polite live region. Skipped on
      // the initial render (announce falsy) so nothing is spoken on page load.
      if (statusEl && announce) {
        statusEl.textContent =
          visible.length === 0
            ? 'No ingredients match this product.'
            : visible.length + (visible.length === 1 ? ' ingredient' : ' ingredients') + ' shown.';
      }

      // A scroll container must be keyboard-reachable, but only while it actually scrolls —
      // a permanent tabindex would add a dead tab stop to the desktop grid.
      if (railActive()) {
        grid.setAttribute('tabindex', '0');
        grid.setAttribute('role', 'group');
        grid.setAttribute('aria-label', 'Ingredients, scrollable');
      } else {
        grid.removeAttribute('tabindex');
        grid.removeAttribute('role');
        grid.removeAttribute('aria-label');
      }
    }

    function onTabClick(e) {
      const btn = e.currentTarget;
      tabs.forEach((tab) => {
        const on = tab === btn;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-pressed', String(on));
      });
      activeFilter = btn.dataset.filter;
      isExpanded = false;
      update(true);
      if (railActive()) grid.scrollTo({ left: 0, behavior: 'instant' });
    }

    function onShowMore() {
      const wasCollapsed = !isExpanded;
      // Captured before the toggle: this is the first card the expansion reveals.
      const revealTarget = wasCollapsed ? items.filter(matches)[CONFIG.initialCount] : null;
      isExpanded = !isExpanded;
      update(true);

      // Collapsing keeps focus on the button. Expanding leaves it there too (the button stays
      // put, relabelled "Show less"), so only move focus to the newly revealed content, which
      // is what the reader pressed for.
      if (revealTarget) {
        const target = revealTarget.querySelector('button') || revealTarget;
        if (target === revealTarget && !target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        target.focus({ preventScroll: true });
      }
    }

    // ---- Detail dialog ----
    const dialog = root.querySelector('[data-detail-dialog]');
    const dialogMedia = root.querySelector('[data-detail-media]');
    const dialogTitle = root.querySelector('[data-detail-title]');
    const dialogBody = root.querySelector('[data-detail-body]');

    function openDetail(e) {
      const card = e.currentTarget.closest('[data-ingredient]');
      const detail = card && card.querySelector('[data-detail]');
      if (!dialog || !detail) return;

      const name = card.querySelector('.ingredient-grid__item-name');
      if (dialogTitle) dialogTitle.textContent = name ? name.textContent.trim() : '';

      if (dialogMedia) {
        dialogMedia.replaceChildren();
        const img = card.querySelector('.ingredient-grid__item-image');
        if (img) {
          const clone = img.cloneNode(true);
          clone.removeAttribute('loading');
          clone.alt = '';
          dialogMedia.appendChild(clone);
        }
      }

      // Cloned, not moved: the card keeps the indexable copy in the page source. Source is
      // merchant richtext from this same document, never user input.
      if (dialogBody) dialogBody.replaceChildren(...[...detail.cloneNode(true).childNodes]);

      dialog.showModal();
    }

    function closeDetail() {
      if (dialog && dialog.open) dialog.close();
    }

    if (dialog) {
      root.querySelectorAll('[data-detail-open]').forEach((btn) => {
        btn.addEventListener('click', openDetail, { signal });
      });
      const closeBtn = root.querySelector('[data-detail-close]');
      if (closeBtn) closeBtn.addEventListener('click', closeDetail, { signal });

      // Click on the backdrop (the dialog element itself, outside its inner box) closes.
      dialog.addEventListener(
        'click',
        (e) => {
          if (e.target === dialog) closeDetail();
        },
        { signal }
      );

      // Drop the cloned copy on close so the detail text isn't duplicated in the DOM while
      // the dialog is idle.
      dialog.addEventListener(
        'close',
        () => {
          if (dialogBody) dialogBody.replaceChildren();
          if (dialogMedia) dialogMedia.replaceChildren();
        },
        { signal }
      );
    }

    // ---- Whole-card affordance ----
    // The card carries cursor:pointer + a lift on hover, so the whole surface must act on
    // click, not just the + button. Page-drawer cards already delegate through the card div's
    // own data-page-drawer, so only description cards (which put the trigger solely on the +
    // button) need this. Keyboard users still tab to the button; this is a pointer nicety.
    root.querySelectorAll('.ingredient-grid__item--interactive').forEach((card) => {
      const detailBtn = card.querySelector('[data-detail-open]');
      if (!detailBtn) return;
      card.addEventListener(
        'click',
        (e) => {
          if (e.target.closest('a, button, [data-detail-open], [data-page-drawer]')) return;
          detailBtn.click();
        },
        { signal }
      );
    });

    tabs.forEach((tab) => tab.addEventListener('click', onTabClick, { signal }));
    if (showMoreBtn) showMoreBtn.addEventListener('click', onShowMore, { signal });
    mobileQuery.addEventListener('change', () => update(false), { signal });

    update(false);
  }

  function teardownRoot(root) {
    const controller = controllers.get(root);
    if (controller) {
      controller.abort();
      controllers.delete(root);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-ingredient-grid]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  // Theme editor: a re-render replaces a section's DOM, so rebind against the new nodes.
  document.addEventListener('shopify:section:load', (e) => {
    const root = document.getElementById('ingredient-grid-' + e.detail.sectionId);
    if (root) initRoot(root);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const root = document.getElementById('ingredient-grid-' + e.detail.sectionId);
    if (root) teardownRoot(root);
  });
})();
